import {registerBrick, CloudObject, Query, QueryResult} from 'olympe';
import {ReactBrick, useProperty, getLogger, useMUITheme} from '@olympeio/core';
import React, {useState, useEffect} from 'react';
import {switchMap, map} from 'rxjs/operators';
import {combineLatest, of} from 'rxjs';
import {format} from 'date-fns';

import {Grid, Box, TextField as MUITextField, Checkbox, FormControlLabel, Typography, Slider, Button as MUIButton, Switch} from '@mui/material';

import {ThemeProvider} from '@mui/material/styles';

import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import {TimePicker} from '@mui/x-date-pickers/TimePicker';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import {DateTimePicker} from '@mui/x-date-pickers/DateTimePicker';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';

const inputTypes = {
    TEXT: 'text',
    NUMBER: 'number',
    CHECKBOX: 'checkbox',
    COLOR: 'color',
    DATE: 'date',
    DATETIME_LOCAL: 'datetime-local',
    EMAIL: 'email',
    PASSWORD: 'password',
    RANGE: 'range',
    TIME: 'time',
    SWITCH: 'switch',
    SPACER: 'spacer',
};

export default class DynamicForm extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        return $.observe('Hidden');
    }

    /**
     * Subscribe to the config datasets to read the configuration of the brick
     * Then subscribe to the data as well to fill the chart js object with the data
     * (This last step is done in the processData method)
     * Finally set the context variable 'graph data' with the chart js dataset object
     *
     * By overriding init() we can set the context variable used in the rendering of the React component
     * and we don't need to override the setupExecution() since Visual Component are rendered only once
     * and then react takes care of further updates
     *
     * @override
     */
    init($) {
        this.logger = getLogger('DynamicForm');
        let modelName = '';
        $.observe('config', false)
            .pipe(
                switchMap((configType) => {
                    if (configType?.query().followRecursively(CloudObject.extendRel).executeFromCache().has('018f2ece64a615790576')) {
                        modelName = configType.name();
                        return configType ? Query.instancesOf(configType, false).observe($) : of(QueryResult.empty());
                    } else {
                        if (!!configType) {
                            if (!$.get(GlobalProperties.EDITION, true)) {
                                throw ErrorFlow.create('The config provided to the dynamic form should extend the Dynamic Form Config object', 0);
                            } else {
                                this.logger.error('The config provided to the dynamic form should extend the Dynamic Form Config object');
                            }
                        }
                        return of(QueryResult.empty());
                    }
                }),
                switchMap((configs) => {
                    if (configs.size() === 0 && modelName.trim() !== '') {
                        this.logger.warn(`The config "${modelName}" does not have any instance`);
                    }
                    return combineLatest(configs.map((config) => config.observeProperties($).pipe(map(() => config))));
                })
            )
            .subscribe((configType) => {
                const formData = configType.map((item) => item.toJSON()).sort((a, b) => a.rank - b.rank);
                $.set('form data', formData);
            });
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {
            const logger = getLogger('DynamicForm');

            const formData = useProperty($, 'form data', true);
            const dataFromJson = useProperty($, 'data', true);
            const spacing = useProperty($, 'spacing', true);
            const lineHeight = useProperty($, 'line height', true);
            const fontSize = useProperty($, 'font size', true);
            const disabled = useProperty($, 'disabled', true);

            const [form, setForm] = useState({});
            const [validationErrors, setValidationErrors] = useState({});

            const theme = useMUITheme($);

            useEffect(() => {
                if (formData && formData.length > 0) {
                    const res = formData.reduce((a, v) => ({...a, [v.property]: dataFromJson?.[v.property]}), {});
                    setForm(res);
                }
            }, [formData, dataFromJson]);

            if (!formData || formData?.length === 0) {
                return null;
            }

            const handleChange = (event, regex) => {
                const propertyName = event.target.name;
                const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
                let errors = {...validationErrors};

                if (regex && value !== undefined && value !== null) {
                    const regexPattern = new RegExp(regex);
                    if (!regexPattern.test(value)) {
                        errors = {...errors, [propertyName]: `Invalid ${propertyName}`};
                    } else {
                        delete errors[propertyName];
                    }
                }

                setForm({
                    ...form,
                    [event.target.name]: value,
                });

                setValidationErrors(errors);
            };

            const handleSave = () => {
                $.set('data', form);
                $.trigger('On Save');
            };

            const generateField = (item) => {
                const error = validationErrors[item.property];

                switch (item.type) {
                    case inputTypes.TEXT:
                    case inputTypes.NUMBER:
                    case inputTypes.EMAIL:
                    case inputTypes.PASSWORD:
                    case inputTypes.COLOR:
                        const commonInputProps = {
                            value: form[item.property] ?? (item.type === inputTypes.COLOR ? '#000000' : ''),
                            label: item.name,
                            name: item.property,
                            type: item.type,
                            fullWidth: true,
                            disabled: disabled,
                            InputProps: {style: {fontSize: fontSize, height: lineHeight}},
                            InputLabelProps: {shrink: true},
                            onChange: (event) => handleChange(event, item.regex),
                        };

                        return (
                            <DynamicForm.ErrorDisplayAndWrapper error={error}>
                                <MUITextField {...commonInputProps} />
                            </DynamicForm.ErrorDisplayAndWrapper>
                        );
                    case inputTypes.DATE:
                    case inputTypes.TIME:
                    case inputTypes.DATETIME_LOCAL:
                        return (
                            <DynamicForm.ErrorDisplayAndWrapper error={error}>
                                <DynamicForm.RangePickerComponent
                                    disabled={disabled}
                                    item={item}
                                    logger={logger}
                                    form={form}
                                    inputType={item.type}
                                    setValidationErrors={setValidationErrors}
                                    validationErrors={validationErrors}
                                    lineHeight={lineHeight}
                                    fontSize={fontSize}
                                    onChange={handleChange}
                                />
                            </DynamicForm.ErrorDisplayAndWrapper>
                        );
                    case inputTypes.CHECKBOX:
                    case inputTypes.SWITCH:
                        const ControlComponent = item.type === inputTypes.CHECKBOX ? Checkbox : Switch;
                        return (
                            <DynamicForm.ErrorDisplayAndWrapper error={error}>
                                <FormControlLabel
                                    sx={{height: lineHeight, marginLeft: 0}}
                                    control={
                                        <ControlComponent
                                            onChange={(event) => handleChange(event, item.regex)}
                                            disabled={disabled}
                                            name={item.property}
                                            checked={form[item.property] || false}
                                        />
                                    }
                                    label={
                                        <Typography variant='body2' sx={{fontSize}}>
                                            {item.name}
                                        </Typography>
                                    }
                                />
                            </DynamicForm.ErrorDisplayAndWrapper>
                        );
                    case inputTypes.RANGE:
                        return (
                            <DynamicForm.ErrorDisplayAndWrapper error={error}>
                                <DynamicForm.RangeSlider
                                    item={item}
                                    form={form}
                                    handleChange={(event) => handleChange(event, item.regex)}
                                    logger={logger}
                                    lineHeight={lineHeight}
                                    fontSize={fontSize}
                                    setForm={setForm}
                                    disabled={disabled}
                                />
                            </DynamicForm.ErrorDisplayAndWrapper>
                        );
                    case inputTypes.SPACER:
                        return <Box sx={{height: lineHeight}}></Box>;
                    default:
                        return <Box sx={{height: lineHeight}}></Box>;
                }
            };

            return (
                <>
                    <ThemeProvider theme={theme}>
                        <Box sx={{flexGrow: 1, overflow: 'auto', height: '100%'}}>
                            <Grid container sx={{paddingTop: 2}} spacing={`${spacing}px`}>
                                {formData.map((item) => {
                                    return (
                                        <Grid item xs={12} key={item['#tag']}>
                                            {generateField(item)}
                                        </Grid>
                                    );
                                })}
                            </Grid>
                            <Box sx={{paddingTop: 2}} textAlign='center'>
                                <Grid container justify='space-between' spacing={2}>
                                    <Grid item md={6} xs={12}>
                                        <MUIButton disabled={disabled} variant='outlined' onClick={() => $.trigger('On Cancel')} sx={{minWidth: '120px'}}>
                                            Cancel
                                        </MUIButton>
                                    </Grid>
                                    <Grid item md={6} xs={12}>
                                        <MUIButton
                                            disabled={disabled || Object.keys(validationErrors).length > 0}
                                            variant='contained'
                                            onClick={handleSave}
                                            sx={{minWidth: '120px'}}>
                                            Save
                                        </MUIButton>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    </ThemeProvider>
                </>
            );
        };
    }
}

registerBrick('018f2ed66c5c0fe48034', DynamicForm);

DynamicForm.RangeSlider = ({item, form, handleChange, logger, lineHeight, fontSize, setForm, disabled}) => {
    const range = DynamicForm.validateAndParseRangeSlider(item, logger) || [0, 100];
    const [min, max] = range.map(Number);
    return (
        <>
            <Box sx={{height: lineHeight * 1.8, padding: '0 10px'}}>
                <Typography id={item.name} gutterBottom sx={{fontSize}}>
                    {item.name}
                </Typography>
                <Slider
                    value={Number(form[item.property]) || 0}
                    name={item.property}
                    valueLabelDisplay='auto'
                    onChange={handleChange}
                    aria-labelledby={item.name}
                    min={min}
                    max={max}
                    disabled={disabled}
                />
                <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                    <Typography
                        variant='body2'
                        onClick={() => !disabled && setForm({...form, [item.property]: min})}
                        sx={{cursor: disabled ? 'default' : 'pointer', fontSize}}>
                        {min} min
                    </Typography>
                    <Typography
                        variant='body2'
                        onClick={() => !disabled && setForm({...form, [item.property]: max})}
                        sx={{cursor: disabled ? 'default' : 'pointer', fontSize}}>
                        {max} max
                    </Typography>
                </Box>
            </Box>
        </>
    );
};

DynamicForm.ErrorDisplayAndWrapper = ({children, error}) => (
    <>
        {children}
        {error && (
            <Box>
                <Typography variant='caption' color='error'>
                    {error}
                </Typography>
            </Box>
        )}
    </>
);

DynamicForm.validateAndParseRangeSlider = (item, logger) => {
    if (!item.range) return undefined;

    var pattern = /^(?:(?:[0-9]|[1-9][0-9])~(?:[0-5]\d|[1-9][0-9]?|100))$/;
    const isValid = pattern.test(item.range);
    if (!isValid) {
        logger.warn(`The specified range ${item.range} for property ${item.property} cannot be parsed, or is out of range.`);
        return undefined;
    } else {
        return item.range.split('~');
    }
};

DynamicForm.validateDateRange = (rangeString, regexPatternt) => {
    const splittedArray = rangeString.split('~');
    const datePatern = new RegExp(regexPatternt);
    return splittedArray.every((date) => datePatern.test(date));
};

DynamicForm.formatDateWithoutTimeZone = (dateString) => {
    if (dateString) {
        const date = new Date(dateString);
        if (date instanceof Date && !isNaN(date)) {
            return format(date, "yyyy-MM-dd'T'HH:mm:ss");
        }
    }
};

DynamicForm.validateAndParseRange = (item, logger) => {
    if (!item.range) return undefined;
    if (!DynamicForm.validateDateRange(item.range, item.regex)) {
        logger.warn(`The specified range ${item.range} for property ${item.property} cannot be parsed, or is out of range.`);
        return undefined;
    }
    return item.range.split('~');
};

DynamicForm.RangePickerComponent = ({item, logger, form, inputType, disabled, setValidationErrors, validationErrors, lineHeight, fontSize, onChange}) => {
    const range = DynamicForm.validateAndParseRange(item, logger);

    const handleSetError = (error) => {
        let message = '';
        switch (error) {
            case 'minTime':
                message = 'Selected min time is out of range';
                break;
            case 'maxTime':
                message = 'Selected max time is out of range';
                break;
            case 'minDate':
                message = 'Selected min date is out of range';
                break;
            case 'maxDate':
                message = 'Selected max date is out of range';
                break;
            default:
                message = 'Invalid date';
        }
        return message;
    };
    const PickerProps = {
        label: item.name,
        disabled: disabled,
        value: form[item.property] ? new Date(form[item.property]) : null,
        textField: (params) => (
            <MUITextField
                sx={{
                    '& .MuiInputBase-input': {
                        height: `${lineHeight}px`,
                        paddingTop: 0,
                        paddingBottom: 0,
                        fontSize: `${fontSize}px`,
                    },
                }}
                {...params}
                fullWidth
            />
        ),

        onChange: (date) => {
            const event = {
                target: {
                    name: item.property,
                    value: date && DynamicForm.formatDateWithoutTimeZone(date),
                    type: 'date',
                },
            };
            onChange(event, item.regex);
        },
        onError: (error) => {
            let errors = {...validationErrors};
            if (error) {
                errors = {...errors, [item.property]: handleSetError(error)};
            } else {
                delete errors[item.property];
            }

            setValidationErrors(errors);
        },
    };
    if (range) {
        if (item.type === inputTypes.DATE) {
            PickerProps.minDate = new Date(range[0]);
            PickerProps.maxDate = new Date(range[1]);
        } else if (item.type === inputTypes.TIME) {
            PickerProps.minTime = new Date(range[0]);
            PickerProps.maxTime = new Date(range[1]);
        } else if (item.type === inputTypes.DATETIME_LOCAL) {
            PickerProps.minDateTime = new Date(range[0]);
            PickerProps.maxDateTime = new Date(range[1]);
        }
    }

    let PickerComponent;
    switch (inputType) {
        case inputTypes.DATE:
            PickerComponent = DatePicker;
            break;
        case inputTypes.TIME:
            PickerComponent = TimePicker;
            break;
        case inputTypes.DATETIME_LOCAL:
            PickerComponent = DateTimePicker;
            break;
        default:
            PickerComponent = null;
    }

    return (
        <>
            {PickerComponent && (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <PickerComponent {...PickerProps} />
                </LocalizationProvider>
            )}
        </>
    );
};
